// Real internet-connectivity check.
//
// navigator.onLine is only reliable when it is false: it stays true on any network
// (a Wi-Fi with no internet, a VPN that dropped, ...). So when a request gets no
// response we ask an external connectivity endpoint before blaming the network.
// Our own origin can't be used: on localhost it answers even when offline.

// The endpoint Chrome itself uses for connectivity checks: tiny, fast, CORS-agnostic
// with mode "no-cors" (an opaque response still proves the network round-trip worked).
const CONNECTIVITY_URL = 'https://www.gstatic.com/generate_204';
const CHECK_TIMEOUT_MS = 5000;

export type ConnectionStatus = 'online' | 'offline';

let inFlight: Promise<ConnectionStatus> | null = null;

const probe = async (): Promise<ConnectionStatus> => {
  if (!navigator.onLine) {
    return 'offline';
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);

  try {
    await fetch(`${CONNECTIVITY_URL}?_=${Date.now()}`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return 'online';
  } catch (_error) {
    return 'offline';
  } finally {
    window.clearTimeout(timer);
  }
};

// Many requests can fail at once; they all share one check.
export const checkConnection = (): Promise<ConnectionStatus> => {
  if (!inFlight) {
    inFlight = probe().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};
